import type { ILeadFormInput } from '../../Modules/LeadModule'
import type { ResAllType } from '../../Types/Api/All'
import type {
  LeadActionCommentType,
  LeadActionType,
  LeadType,
  LeadTypeV3,
} from '../../Types/Api/Lead'
import { api } from './API'
import { isResErrorType, requireAuth } from './Auth'

//
// 見込み客
//
declare global {
  interface Navigator {
    msSaveBlob: (blobOrBase64: Blob | string, filename: string) => void
  }
}

interface Condition {
  name: string
  type: string
  display_name: string
  expression: string
  value: string
}

type Conditions = Condition[][]

export const searchLeads = async (
  page: number,
  conditions = {},
  filter_groups: Conditions | undefined,
  signal?: AbortSignal,
) => {
  const res = await requireAuth<LeadTypeV3>(
    // searchLeadsのみ、v3を呼びだす
    api.get(
      `v3/lead/leads`, // 'leads',
      { page, conditions, filter_groups: JSON.stringify(filter_groups) },
      { signal },
    ),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  const leads = res.data.leads
  const pagination = res.data.pagination

  return {
    ...res,
    data: {
      leads: leads.map((lead) => ({
        ...lead,
      })),
       ...pagination,
    },
  };
  
}

export const findLead = async (id: number) => {
  const res = await requireAuth<LeadType>(api.get(`leads/${id}`))

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const searchLeadActions = async (id: number, page: number) => {
  const res = await requireAuth<ResAllType<LeadActionType>>(
    api.get(`leads/${id}/actions`, { page }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return {
    ...res,
    data: {
      ...res.data,
      data: res.data.data.map((d) => ({
        ...d,
      })),
    },
  }
}

export const storeLead = async (formInput: ILeadFormInput) => {
  const res = await requireAuth<LeadType>(api.post('leads', formInput))

  return res
}

export const csvDownload = async (
  conditions = {},
  csvData: object,
  page: number,
  lead_conditions: any[],
  filter_groups: any[],
) => {
  const res = await requireAuth<string>(
    api.post('leads/csvDownload', {
      conditions,
      display_settings: JSON.stringify(csvData),
      page,
      lead_conditions,
      filter_groups: JSON.stringify(filter_groups),
    }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  const fileName = 'リード一覧.csv'
  const bom = new Uint8Array([0xef, 0xbb, 0xbf])
  const file = new Blob([bom, res.data], { type: 'text/csv;charset=utf-8' })
  // @ts-ignore
  if (window.navigator.msSaveOrOpenBlob) {
    navigator.msSaveBlob(file, fileName)
  } else {
    const dlBtn = document.createElement('a')
    dlBtn.download = fileName
    dlBtn.href = URL.createObjectURL(file)
    dlBtn.click()
  }

  return res
}

export const addLeadAction = async (actions, updaterId) => {
  const postData = { ...actions, updated_by: updaterId }
  const res = await requireAuth<LeadActionType>(
    api.post('lead_actions', postData),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const summaryDocument = async (id: number) => {
  const res = await requireAuth<any>(
    api.post(`lead_actions/document_summary`, { lead_id: id }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const deleteLeadAction = async (lead_action_id: number) => {
  const res = await requireAuth<LeadActionType>(
    api.delete(`lead_actions/${lead_action_id}`),
  )

  return res
}

export const updateLeadAction = async (
  lead_action_id: number,
  lead_id: number,
  title: string,
  type: string,
) => {
  const res = await requireAuth<LeadActionType>(
    api.post(`lead_actions/${lead_action_id}`, { lead_id, title, type }),
  )

  return res
}

export const findLeadAction = async (leadActionId: number, leadId: number) => {
  const res = await requireAuth<LeadActionType>(
    api.get(`lead_actions/${leadActionId}`, { lead_id: leadId }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const archive = async (id: number | null) => {
  const res = await requireAuth<LeadType>(
    api.post(`leads/bulk/archive`, { ids: [id] }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const unarchive = async (id: number | null) => {
  const ids = Array.isArray(id) ? id : [id]
  const res = await requireAuth<LeadType>(
    api.post(`leads/bulk/unarchive`, { ids: ids }),
  )
  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const destroy = async (id: number) => {
  const res = await requireAuth(api.delete(`leads/${id}`))
  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const addComment = async (
  leadId: number,
  leadActionId: number,
  updatedBy: number,
  comment: string,
) => {
  const res = await requireAuth<LeadActionCommentType>(
    api.post(`lead_actions/${leadActionId}/comments`, {
      comment: comment,
      lead_id: leadId,
      updated_by: updatedBy,
    }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const deleteComment = async (
  leadActionId: number,
  commentId: number,
) => {
  const res = await requireAuth(
    api.delete(`lead_actions/${leadActionId}/comments/${commentId}`),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const updateComment = async (
  updated_by: number,
  leadId: number,
  leadActionId: number,
  commentId: number,
  comment: string,
) => {
  const res = await requireAuth<LeadActionCommentType>(
    api.put(`lead_actions/${leadActionId}/comments/${commentId}`, {
      comment: comment,
      lead_id: leadId,
      updated_by: updated_by,
    }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const updateLead = async (id: number, formInput: ILeadFormInput) => {
  const res = await requireAuth<any>(api.put(`leads/${id}`, formInput))

  return res
}

// property_idには、propertyに場合にはpropertyのid、担当者の場合にはowner_idが入る
export const bulkUpdate = async (
  ids: number[],
  property_id: number | string,
  property_value: string | number,
) => {
  const res = await requireAuth<any>(
    api.post(`leads/bulk/update`, { ids, property_id, property_value }),
  )

  return res
}

export const bulkDelete = async (ids: number[]) => {
  const res = await requireAuth<any>(api.post(`leads/bulk/delete`, { ids }))

  return res
}

export const csvUpload = async (file: File) => {
  const formData = new FormData()
  formData.append('csv_file', file)
  const res = await requireAuth<any>(
    api.post('leads/csv/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  )

  return res
}

export const validation = async (data, field_bindings) => {
  const formData = new FormData()
  formData.append('csv_file', data)
  formData.append('field_bindings', JSON.stringify(field_bindings))
  const res = await requireAuth<any>(
    api.post('leads/csv/validation', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  )

  return res
}

export const importCSV = async (file, field_bindings, listName?) => {
  const formData = new FormData()
  formData.append('csv_file', file)
  formData.append('field_bindings', JSON.stringify(field_bindings))
  formData.append('list_name', listName)
  const res = await requireAuth<any>(
    api.post('leads/csv/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  )

  return res
}

export const csvHistory = async () => {
  const res = await requireAuth<any>(api.get('leads/csv/index'))

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return res
}

export const failedCsvDownload = async (id: number) => {
  const res = await requireAuth<any>(api.get(`leads/csv/download/${id}`))
  return res
}
